import Loader from '@components/Loader/Loader';
import { faLock, faStars } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { classNames } from '@modules/classname-helpers';

import styles from './Button.module.scss';

type Flair = 'new' | 'locked' | undefined;
export interface ButtonProps extends React.HtmlHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'subtle' | 'text' | 'dark' | 'grey-border';
  type?: 'default' | 'primary' | 'danger';
  size?: 'tiny' | 'inline' | 'small' | 'medium' | 'large';
  /** Can be missing if the button is a submit type and it is inside a form */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  loading?: boolean;
  theme?: 'light' | 'dark';
  disabled?: boolean;
  /** Styles the button as disabled without setting the disabled attribute on the button */
  pseudoDisabled?: boolean;
  submit?: boolean;
  /** Force the button to take the full width, instead of the default inline-block behaviour */
  isFullWidth?: boolean;
}

const Button = ({
  ref: forwardedRef,
  children,
  className,
  variant = 'primary',
  size = 'medium',
  type = 'primary',
  onClick,
  loading = false,
  theme = 'light',
  disabled = false,
  pseudoDisabled = false,
  submit = false,
  isFullWidth = true,
  ...rest
}: React.PropsWithChildren<ButtonProps> & {
  ref?: React.Ref<HTMLButtonElement>;
}) => {
  const { ...buttonProps } = rest;

  return (
    <button
      className={classNames(styles['button'], className)}
      data-variant={variant}
      data-type={type}
      data-size={size}
      data-loading={loading}
      data-theme={theme}
      data-is-full-width={isFullWidth}
      onClick={(event) => {
        event.stopPropagation();
        if (!loading && !pseudoDisabled) {
          onClick?.(event);
        }
      }}
      ref={forwardedRef}
      disabled={disabled}
      data-disabled={disabled || pseudoDisabled}
      tabIndex={pseudoDisabled ? -1 : undefined}
      aria-disabled={disabled || pseudoDisabled}
      type={submit ? 'submit' : 'button'}
      {...buttonProps}
    >
      <div className={styles['button-content']}>{children}</div>
      <div className={styles['loader-container']}>
        <Loader />
      </div>
    </button>
  );
};

function withFlair<T>(
  WrappedComponent: React.ComponentType<React.PropsWithChildren<T>>,
  flair: Flair,
): React.FC<React.PropsWithChildren<T>> {
  // Try to create a nice displayName for React Dev Tools.
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  // Creating the inner component. The calculated Props type here is the where the magic happens.
  const ComponentWithFlair: React.FC<React.PropsWithChildren<T>> = (props) => {
    // props comes afterwards so the can override the default ones.
    return (
      <div className={styles['flair-container']}>
        {flair ? (
          <span className={styles['flair']} data-flair={flair}>
            {flair === 'new' ? (
              <>
                <FontAwesomeIcon size="sm" className={styles['flair-icon']} icon={faStars} />
                New
              </>
            ) : null}
            {flair === 'locked' ? (
              <>
                <FontAwesomeIcon className={styles['flair-icon']} icon={faLock} />
              </>
            ) : null}
          </span>
        ) : null}
        {<WrappedComponent {...props} />}
      </div>
    );
  };

  ComponentWithFlair.displayName = `withFlair(${displayName})`;

  return ComponentWithFlair;
}

const ButtonIcon: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <span className={styles['button-icon']}>{children}</span>
);

Button.displayName = 'Button';

export default Button;
export { ButtonIcon, withFlair };
